import React from 'react';
import { Link } from 'react-router-dom';

export function TermsAndConditions(): React.JSX.Element {
  return (
    <div className="flex flex-col min-h-[50vh] p-4 max-w-3xl mx-auto">
      <h1>Terms & Conditions</h1>
      <div className="text-medium-emphasis-font my-4 space-y-6">
        <p className="text-sm">Last Updated: 12/16/2025</p>

        <div>
          <p>
            <strong>1. Introduction</strong>
            <br />
            {'Welcome to Proprio, Inc. ("we," "us," or "our"). These Terms and Conditions '}
            {"(\"Terms\") govern your access to and use of Proprio's AI surgical data analytics "}
            {'platform, including all related web applications, APIs, mobile apps, dashboards, '}
            SMS communications, and other services (collectively, the &quot;Services&quot;).
          </p>
          <p className="mt-4">
            By using our Services, you agree to be bound by these Terms. If you do not agree, do not use the Services.
          </p>
        </div>

        <p>
          <strong>2. Eligibility and Account Registration</strong>
          <br />
          {'You must be at least 18 years old and authorized by your organization (e.g., '}
          {'hospital, clinic, or healthcare institution) to use the Platform. You are '}
          {'responsible for maintaining the confidentiality of your account credentials '}
          and for all activity under your account.
        </p>

        <div>
          <p>
            <strong>3. Description of Services</strong>
            <br />
            {'Proprio provides tools and analytics designed to enhance surgical workflow '}
            {'insights, documentation accuracy, and case reporting through artificial '}
            intelligence (&quot;AI&quot;) and data analytics. Features may include:
          </p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li>Surgical case logging and reporting;</li>
            <li>Video and imaging review;</li>
            <li>AI-based tagging and data interpretation;</li>
            <li>Communication via email or SMS (e.g., alerts, summaries, notifications); and</li>
            <li>Performance and operational analytics.</li>
          </ul>
          <p className="mt-4">
            {'We may update or modify these Services from time to time by including such '}
            {'alteration and/or modification in these Terms and Conditions, along with a '}
            {'notice of the effective date of such modified Terms and Conditions. Any '}
            {'continued use by you of the Services after the posting of such modified Terms '}
            {'of Conditions shall be deemed to indicate your agreement to such modification. '}
            {'Accordingly, if at any time you do not agree to be subject to any modified '}
            Terms of Conditions, you may no longer use the Services.
          </p>
        </div>

        <div>
          <p>
            <strong>4. SMS/Text Messaging</strong>
            <br />
            By providing your phone number, you consent to receive SMS messages from Proprio for purposes including:
          </p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li>Account verification;</li>
            <li>System alerts or case status notifications;</li>
            <li>Security and login updates;</li>
            <li>Reminders or data review prompts; and</li>
            <li>Important account or service announcements.</li>
          </ul>
          <p className="mt-4">
            {'Message frequency may vary. Message and data rates may apply depending on your '}
            {'carrier. You may opt out of receiving SMS messages at any time by replying STOP '}
            {'to any message or adjusting your account settings. Even after opting out, we '}
            {'may still send non-promotional messages such as security alerts or critical '}
            account notifications.
          </p>
        </div>

        <div>
          <p>
            <strong>5. Data Ownership and Use</strong>
            <br />
            {'You retain ownership of your data and any surgical or patient-related '}
            {'information you upload or process through the Platform ("User Data").'}
          </p>
          <p className="mt-4">
            {'By using the Services, you grant Proprio a limited, non-exclusive, worldwide '}
            license to use, store, and process your User Data solely for:
          </p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li>Providing and improving the Services;</li>
            <li>Developing aggregated, de-identified analytics; and</li>
            <li>Meeting legal or regulatory obligations.</li>
          </ul>
          <p className="mt-4">
            {'Proprio does not sell or share identifiable health data with third parties '}
            for marketing or unrelated purposes.
          </p>
          <p className="mt-4">
            {'Our collection and use of data through the Services is governed by, and described in more detail in our '}
            <Link
              to="/privacy-policy"
              className="underline hover:opacity-80"
            >
              Privacy Policy
            </Link>.
          </p>
        </div>

        <div>
          <p>
            <strong>6. Compliance with Healthcare Regulations</strong>
            <br />
            {'Proprio is committed to maintaining compliance with applicable healthcare data '}
            {'privacy laws, including the Health Insurance Portability and Accountability '}
            Act (&quot;HIPAA&quot;), where applicable.
          </p>
          <p className="mt-4">
            {'Our systems employ encryption, access controls, and audit logs to protect data '}
            {'integrity and confidentiality. Your organization remains responsible for '}
            ensuring compliance within its own systems and workflows.
          </p>
        </div>

        <div>
          <p>
            <strong>7. User Account</strong>
            <br />
            {'To access and use any features of the Services, you are required to open an '}
            {'account ("Your Account") by registering with us. When you register for Your '}
            {'Account you must provide true, accurate, current and complete information, '}
            and you agree to update the account information in order to ensure that it is current.
          </p>
          <p className="mt-4">
            {'As a condition of being permitted to open a user account, you represent that '}
            {'you (i) have the legal capacity (including, without limitation, being of '}
            {'sufficient age) to enter into contracts under the law of the jurisdiction in '}
            {'which you reside, (ii) are not on a list of persons barred you from receiving '}
            {'services under U.S. laws (including, without limitation, the Denied Persons '}
            {'List and the Entity List issued by the U.S. Department of Commerce, Bureau of '}
            {'Industry and Security) or other applicable jurisdiction and (iii) are not a '}
            resident of Cuba, Iran, North Korea, Sudan or Syria.
          </p>
          <p className="mt-4">
            {'You are responsible for maintaining the confidentiality of your passwords and '}
            {'for all of your activities and those of any third party that occur through '}
            {'your user account, whether or not authorized by you. You agree to immediately '}
            {'notify us of any suspected or actual unauthorized use of your user account. '}
            {'You agree that we will not under any circumstances be liable for any cost, '}
            {'loss, damages or expenses arising out of a failure by you to maintain the '}
            security of your passwords.
          </p>
        </div>

        <div>
          <p>
            <strong>8. Restrictions on Use</strong>
            <br />
            You agree not to:
          </p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li>Use the Services for unlawful or unauthorized purposes;</li>
            <li>Attempt to reverse-engineer or interfere with our systems;</li>
            <li>Upload data you do not have rights to; or</li>
            <li>Misuse the Services to process data outside of its intended healthcare or analytics purpose.</li>
          </ul>
        </div>

        <div>
          <p>
            <strong>9. Intellectual Property</strong>
            <br />
            {'All intellectual property rights in the Platform and Services, including '}
            {'software, AI models, and content (other than User Data), are owned by Proprio. '}
            {'You are granted a limited, non-transferable license to use the Platform in '}
            accordance with these Terms.
          </p>
          <p className="mt-4">
            {'Any and all (i) suggestions for correction, change and modification to the '}
            {'Services and other feedback (including but not limited to quotations of '}
            {'written or oral feedback), information and reports provided to us by you '}
            {'(collectively "Feedback"), and all (ii) improvements, updates, modifications '}
            {'or enhancements, whether made, created or developed by us or otherwise '}
            {'relating to the Services ("Revisions"), are and will remain our property. '}
            {'You acknowledge and expressly agree that any contribution of Feedback or '}
            {'Revisions does not and will not give or grant you any right, title or '}
            {'interest in the Services or in any such Feedback or Revisions. You agree to '}
            {'assign to us any and all right, title and interest (including, but not '}
            {'limited to, any patent, copyright, trade secret, trademark, show-how, '}
            {'know-how, moral rights and any and all other intellectual property right) '}
            that you may have in and to any and all Feedback and Revisions.
          </p>
        </div>

        <p>
          <strong>10. Disclaimers</strong>
          <br />
          {'The Services are provided "as is" and "as available." Proprio does not '}
          {'guarantee uninterrupted or error-free operation. Analytical outputs are for '}
          {'informational and educational use only and are not intended to replace '}
          professional medical judgment.
        </p>

        <p>
          <strong>11. Indemnification</strong>
          <br />
          {'By entering into these Terms and Conditions and using the Services, you agree '}
          {'that you shall defend, indemnify and hold us, our affiliates, officers, '}
          {'directors, members, employees, attorneys and agents harmless from and against '}
          {'any and all claims, costs, damages, losses, liabilities and expenses (including '}
          {"attorneys' fees and costs) arising out of or in connection with: (a) your "}
          {'violation or breach of any term of these Terms of Conditions or any applicable '}
          {'law or regulation; (b) your violation of any rights of any third party; (c) any '}
          {'unauthorized use of the Service; (d) your negligence or willful misconduct; or '}
          (e) heath care decision-making, patient care, or treatment outcomes.
        </p>

        <p>
          <strong>12. Limitation of Liability</strong>
          <br />
          {'TO THE FULLEST EXTENT PERMITTED BY LAW, PROPRIO SHALL NOT BE LIABLE FOR ANY '}
          {'INDIRECT, INCIDENTAL, OR CONSEQUENTIAL DAMAGES ARISING FROM YOUR USE OF THE '}
          {"SERVICES. PROPRIO'S TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT PAID BY YOU "}
          {'(OR YOUR ORGANIZATION) FOR USE OF THE SERVICES IN THE 12 MONTHS PRECEDING '}
          THE CLAIM.
        </p>

        <p>
          <strong>13. Termination</strong>
          <br />
          {'Proprio may suspend or terminate your access to the Services for any violation '}
          {'of these Terms or at the request of your affiliated institution. Upon '}
          termination, your right to use the Services immediately ceases.
        </p>

        <p>
          <strong>14. Governing Law</strong>
          <br />
          {'These Terms are governed by and construed in accordance with the laws of the '}
          State of Washington, without regard to its conflict of laws principles.
        </p>

        <div>
          <p>
            <strong>15. Contact</strong>
            <br />
            For any questions regarding these Terms, please contact:
          </p>
          <p className="mt-2">
            Email: DataApps@propriovision.com
          </p>
          <p>
            Address: 111 W John St Suite 308, Seattle, WA 98119
          </p>
        </div>
      </div>
    </div>
  );
}

export default TermsAndConditions;
